"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.proceedShortlistSchema = exports.answerShortlistSchema = exports.MANAGER_RESPONSE_LABELS = exports.SHORTLIST_COMMENT_MAX = exports.SHORTLIST_REASON_MAX = exports.MANAGER_NO_REASON_CODE = exports.SHORTLIST_REMINDER_WORKING_DAYS = exports.reviewBringBackSchema = exports.reviewMoveSchema = exports.rateReviewLineSchema = exports.REVIEW_RATING_MAX = exports.REVIEW_RATING_MIN = exports.REVIEW_STATUS_LABELS = exports.REVIEW_QUEUE_STATUSES = void 0;
exports.compareReviewQueue = compareReviewQueue;
exports.hrStarsOf = hrStarsOf;
exports.shortlistRecipients = shortlistRecipients;
exports.canChangeAnswer = canChangeAnswer;
exports.topMatchesOf = topMatchesOf;
exports.shortlistCounts = shortlistCounts;
exports.isShortlistDone = isShortlistDone;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const state_machines_1 = require("./state-machines");
const schemas_1 = require("./schemas");
// R6 — HR review and the shortlist hand-off (§L31, F23.06–F23.08, F24.01–F24.05; screens
// SCR-01, SCR-03, SHL-01, SHL-02, HM-02). Decisions: DECISIONS-LOG "Start R6", ADR 0041.
// --- Review (inside `screening`) ---------------------------------------------------------
/** §L31: the queue is To review, then Maybe once To review is empty. */
exports.REVIEW_QUEUE_STATUSES = ['to_review', 'maybe'];
exports.REVIEW_STATUS_LABELS = {
    to_review: 'To review',
    shortlisted: 'Shortlisted',
    maybe: 'Maybe',
};
/**
 * §L31 "Order: final score, highest first, then import order. Maybe comes back round once
 * To review is empty." A null status counts as To review (§L25 starts every one there).
 * ADR 0062: "final score" is the rank key, which is the final score for a job ranked by criteria.
 */
function compareReviewQueue(a, b) {
    const isMaybe = (row) => (row.reviewStatus === 'maybe' ? 1 : 0);
    if (isMaybe(a) !== isMaybe(b))
        return isMaybe(a) - isMaybe(b);
    if (a.rankKey !== b.rankKey) {
        if (a.rankKey === null)
            return 1;
        if (b.rankKey === null)
            return -1;
        return b.rankKey - a.rankKey;
    }
    return a.importOrder - b.importOrder || a.id.localeCompare(b.id);
}
/** JB-13: HR rates the matrix lines ticked "HR review", 1 to 5 (§L31). */
exports.REVIEW_RATING_MIN = 1;
exports.REVIEW_RATING_MAX = 5;
/** §L31 "HR ★": the mean of HR's ratings, to one decimal. It never touches the ATS score. */
function hrStarsOf(scores) {
    if (scores.length === 0)
        return null;
    const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    return Math.round(mean * 10) / 10;
}
/** F23.06: one line's rating; null takes it away. */
exports.rateReviewLineSchema = zod_1.z.object({
    score: zod_1.z.number().int().min(exports.REVIEW_RATING_MIN).max(exports.REVIEW_RATING_MAX).nullable(),
});
/** The conveyor's lanes and RNK-01's "select → Shortlist" (F23.07), and their 10 s Undo (F23.08). */
exports.reviewMoveSchema = zod_1.z.object({
    applicationIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Select at least one.').max(200, 'At most 200 at a time.'),
    to: zod_1.z.enum(enums_1.REVIEW_STATUSES),
});
/**
 * F23.18 (ADR 0057): someone rejected in review brought back to a lane, any time while the job is
 * open and their regret letter is still unsent (owner, 2026-10-25).
 */
exports.reviewBringBackSchema = zod_1.z.object({
    applicationId: schemas_1.uuidSchema,
    to: zod_1.z.enum(enums_1.REVIEW_STATUSES),
});
// --- The shortlist -------------------------------------------------------------------------
/** §L31: one reminder after this many working days on the manager's calendar, per send (Q4). */
exports.SHORTLIST_REMINDER_WORKING_DAYS = 2;
/** §L19 "Not chosen by the hiring manager": preselected by "Reject the No's" (§L31). */
exports.MANAGER_NO_REASON_CODE = 'SCR-HM-NO';
exports.SHORTLIST_REASON_MAX = 200;
exports.SHORTLIST_COMMENT_MAX = 1000;
/** The manager's answer, as staff read it (design card "Review and shortlist"). */
exports.MANAGER_RESPONSE_LABELS = {
    not_sent: 'Not sent',
    awaiting: 'Waiting',
    yes: 'Yes, interview',
    no: 'No',
    hr_proceeded: 'HR went ahead',
};
/** §L31: the requester and the line manager, once each when they're the same person. */
function shortlistRecipients(job) {
    return [...new Set([job.requestedByUserId, job.lineManagerUserId].filter((id) => id !== null))];
}
/**
 * §L25 Manager response plus §L31's Done rule (DECISIONS-LOG "Start R6" Q6): the requester
 * or line manager answers until Done; an HR Lead answers on their behalf before Done and
 * changes an answer after; a recruiter changes an answer only after Done.
 */
function canChangeAnswer(actor, from, to, isDone) {
    if (!(0, state_machines_1.canTransition)(state_machines_1.MANAGER_RESPONSE_MACHINE, from, to, actor))
        return false;
    if (actor === 'owner')
        return !isDone;
    if (actor === 'recruiter')
        return isDone;
    return true;
}
exports.answerShortlistSchema = zod_1.z
    .object({
    answer: zod_1.z.enum(['yes', 'no']),
    reason: zod_1.z.string().trim().max(exports.SHORTLIST_REASON_MAX, `At most ${exports.SHORTLIST_REASON_MAX} characters.`).nullable().default(null),
    /** An optional §L19 code beside the manager's own words. */
    reasonCode: zod_1.z.string().trim().min(1).max(40).nullable().default(null),
    comment: zod_1.z.string().trim().max(exports.SHORTLIST_COMMENT_MAX, `At most ${exports.SHORTLIST_COMMENT_MAX} characters.`).nullable().default(null),
    version: zod_1.z.number().int().positive(),
})
    .superRefine((input, ctx) => {
    if (input.answer === 'no' && (input.reason ?? '') === '') {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Say briefly why not.' });
    }
});
/** F24.05 "Move to contact" on candidates still waiting for the manager. */
exports.proceedShortlistSchema = zod_1.z.object({
    applicationIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Select at least one.').max(200, 'At most 200 at a time.'),
});
/** SHL-01 "the top 3 criteria marks": the heaviest three, the job's order breaking a tie. */
function topMatchesOf(matches) {
    const TOP = 3;
    return matches
        .map((match, index) => ({ match, index }))
        .sort((a, b) => b.match.weight - a.match.weight || a.index - b.index)
        .slice(0, TOP)
        .map(({ match }) => ({ label: match.label, mark: match.mark }));
}
function shortlistCounts(candidates) {
    const counts = { notSent: 0, awaiting: 0, yes: 0, no: 0, proceeded: 0 };
    for (const { answer } of candidates) {
        if (answer === 'not_sent')
            counts.notSent += 1;
        if (answer === 'awaiting')
            counts.awaiting += 1;
        if (answer === 'yes')
            counts.yes += 1;
        if (answer === 'no')
            counts.no += 1;
        if (answer === 'hr_proceeded')
            counts.proceeded += 1;
    }
    return counts;
}
/** Done once every send has its Done; a later send opens it again (DECISIONS-LOG "Start R6" Q3). */
function isShortlistDone(sends) {
    return sends.length > 0 && sends.every((send) => send.completedAt !== null);
}
//# sourceMappingURL=shortlist.js.map